import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Input,
  Button,
  VStack,
  HStack,
  Text,
  Avatar,
  useDisclosure,
  useColorModeValue,
  Slide,
  Flex,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { ChatIcon, CloseIcon } from "@chakra-ui/icons";
import axios from "axios";
import aiAvatar from "../Assets/Ai.png";

const FloatingAIAssistant = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [messages, setMessages] = useState([
    { 
      from: "bot", 
      text: "Hello! I'm your Agro Assistant. How can I help you with your farming questions today?", 
      timestamp: new Date() 
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const toast = useToast();

  // Color mode values for consistent theming
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const userMsgBg = useColorModeValue("green.500", "green.600");
  const botMsgBg = useColorModeValue("gray.100", "gray.700");
  const botMsgColor = useColorModeValue("gray.800", "white");

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { from: "user", text: input, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const res = await axios.post("https://api.agromanch.com/api/ai/chatbot", { message: input });
      
      // Handle both success and error responses
      if (res.data.success === false) {
        const errorMessage = res.data.error || "Something went wrong. Please try again.";
        const botMessage = { from: "bot", text: errorMessage, timestamp: new Date() };
        setMessages(prev => [...prev, botMessage]);
      } else {
        const botMessage = { 
          from: "bot", 
          text: res.data.answer || res.data.reply || "I'm here to help with your farming questions!", 
          timestamp: new Date(),
          products: res.data.products || []
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (err) {
      console.error("Chatbot error:", err);
      const errorMsg = { 
        from: "bot", 
        text: "Unable to connect to the assistant. Please make sure you're online.", 
        timestamp: new Date() 
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Button */}
      <Slide direction="bottom" in={!isOpen} style={{ zIndex: 10 }}>
        <Box
          position="fixed"
          bottom="80px"
          right="20px"
          zIndex={9999}
        >
          <IconButton
            icon={<ChatIcon />}
            onClick={onOpen}
            colorScheme="green"
            borderRadius="full"
            size="lg"
            boxShadow="lg"
            aria-label="Open AI Assistant"
            title="Agro Assistant"
          />
        </Box>
      </Slide>

      {/* Chat Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent bg={bgColor}>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
            <HStack spacing={3}>
              <Avatar 
                src={aiAvatar} 
                name="Agro Assistant" 
                size="sm" 
                bg="green.500"
              />
              <Box>
                <Text fontWeight="bold">Agro Assistant</Text>
                <Text fontSize="xs" color="gray.500">Always here to help</Text>
              </Box>
            </HStack>
          </DrawerHeader>

          <DrawerBody p={0}>
            {/* Messages Container */}
            <VStack 
              align="stretch" 
              spacing={0} 
              height="calc(100vh - 140px)"
              overflow="hidden"
            >
              <Box 
                flex="1" 
                overflowY="auto" 
                p={4}
                display="flex"
                flexDirection="column"
              >
                {messages.map((msg, index) => (
                  <Box 
                    key={index} 
                    alignSelf={msg.from === "user" ? "flex-end" : "flex-start"}
                    maxWidth="80%"
                    mb={3}
                  >
                    <HStack spacing={2} justify={msg.from === "user" ? "flex-end" : "flex-start"}>
                      {msg.from === "bot" && (
                        <Avatar 
                          src={aiAvatar} 
                          name="Agro Assistant" 
                          size="xs" 
                          bg="green.500"
                        />
                      )}
                      <Box>
                        <Box
                          bg={msg.from === "user" ? userMsgBg : botMsgBg}
                          color={msg.from === "user" ? "white" : botMsgColor}
                          borderRadius="lg"
                          px={4}
                          py={2}
                          maxWidth="100%"
                        >
                          <Text fontSize="sm" whiteSpace="pre-wrap">{msg.text}</Text>
                        </Box>
                        <Text 
                          fontSize="xs" 
                          color="gray.500" 
                          textAlign={msg.from === "user" ? "right" : "left"}
                          mt={1}
                        >
                          {formatTime(msg.timestamp)}
                        </Text>
                      </Box>
                      {msg.from === "user" && (
                        <Avatar 
                          src={localStorage.getItem("authpicture")} 
                          name={localStorage.getItem("authname")} 
                          size="xs" 
                        />
                      )}
                    </HStack>
                  </Box>
                ))}
                {isLoading && (
                  <Box alignSelf="flex-start" mb={3}>
                    <HStack spacing={2}>
                      <Avatar 
                        src={aiAvatar} 
                        name="Agro Assistant" 
                        size="xs" 
                        bg="green.500"
                      />
                      <HStack spacing={1}>
                        <Spinner size="xs" color="green.500" />
                        <Text fontSize="sm" color="gray.500">Thinking...</Text>
                      </HStack>
                    </HStack>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Input Area */}
              <Box 
                p={4} 
                borderTopWidth="1px" 
                borderColor={borderColor}
              >
                <HStack>
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about crops, diseases, or farming tips..."
                    borderRadius="full"
                    size="md"
                    disabled={isLoading}
                  />
                  <IconButton
                    icon={isLoading ? <Spinner size="sm" /> : <ChatIcon />}
                    onClick={sendMessage}
                    colorScheme="green"
                    borderRadius="full"
                    aria-label="Send message"
                    isDisabled={!input.trim() || isLoading}
                  />
                </HStack>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FloatingAIAssistant;